// report_selectors.ts — pure selectors for M2 council_report (FE_SPEC §10).
//
// Anchor: PRD §7.7 (9-section schema · 10 scene-symbol matrix) +
//         BE_SPEC §3 CouncilReport struct.
//
// All selectors take `report: any` (BE schema is in flight; M0 5-section legacy
// shape coexists with M2 9-section). They return null when the M2 field is
// absent so the Report.tsx call-sites can render conditionally.
//
// ★ FIX-C5 / FIX-C6: refund + quality_compromised live at ROOT (NOT inside
//   metadata.{refund_status, quality_compromised}). Selectors enforce this so
//   Report.tsx never reads the wrong path.

import type { SceneSymbolId } from '@/api/m0_sessions';

export interface CouncilHealthDTO {
  iron_kills_count: number;
  v9_warnings: number;
  fallback_used: boolean;
  gating_user_advances: number;
  phase_count: number;
  role_distinct_count: number;
  total_llm_calls: number;
}

export interface QualityCompromisedDTO {
  flag: boolean;
  reasons: Array<
    | 'no_iron_kills'
    | 'fallback_used'
    | 'low_attachment_citation'
    | 'content_filter_partial'
    | 'composer_invariant_warning'
  >;
  free_retry_eligible: boolean;
  rationale?: string;
}

export type RefundStatus =
  | 'no_refund'
  | 'full_refund'
  | 'partial_refund'
  | 'processing'
  | 'failed';

export interface RefundDTO {
  amount_pct: 0 | 50 | 100;
  reason: string;
  processed_at: string;
  status: RefundStatus;
}

/** §10.4 council_health 透明度模块 selector. */
export function selectCouncilHealth(report: unknown): CouncilHealthDTO | null {
  const r = report as { council_health?: CouncilHealthDTO } | undefined;
  if (!r?.council_health) return null;
  return r.council_health;
}

/**
 * §10.5 P0-9 · FIX-C5: 读 **root** `report.quality_compromised`.
 * 仅当 `flag === true` 时返非空 (Report.tsx 据此决定是否 mount banner).
 */
export function selectQualityCompromised(report: unknown): QualityCompromisedDTO | null {
  const r = report as { quality_compromised?: QualityCompromisedDTO } | undefined;
  const qc = r?.quality_compromised;
  if (!qc || !qc.flag) return null;
  return qc;
}

/**
 * §10.6 P0-13 · FIX-C6: 读 **root** `report.refund`.
 * 仅 `status !== 'no_refund'` 时返非空 (no_refund chip 不渲染 per spec).
 */
export function selectRefund(report: unknown): RefundDTO | null {
  const r = report as { refund?: RefundDTO } | undefined;
  const ref = r?.refund;
  if (!ref || ref.status === 'no_refund') return null;
  return ref;
}

// ── §10.2 scene-symbol → required sections matrix (PRD §7.7 真值) ──
// 12 scene_symbols (10 + 1 unknown) × 9 sections (5 always + 4 conditional).

type ReportSection =
  | 'metadata'
  | 'decision'
  | 'candidates'
  | 'iron_dissent'
  | 'validation_checklist'
  | 'attachment_citations'
  | 'open_questions'
  | 'next_actions'
  | 'council_health';

const ALWAYS_REQUIRED: ReportSection[] = [
  'metadata',
  'decision',
  'iron_dissent', // always · standing_objections may be empty array
  'open_questions',
  'council_health',
];

const SCENE_REQUIRED_SECTIONS: Record<SceneSymbolId, ReadonlySet<ReportSection>> = {
  decision_high_stakes: new Set([
    'decision', 'candidates', 'iron_dissent',
    'validation_checklist', 'attachment_citations', 'next_actions',
  ]),
  decision_low_stakes: new Set(['decision', 'candidates']),
  technical_evaluation: new Set([
    'decision', 'candidates', 'iron_dissent',
    'validation_checklist', 'attachment_citations',
  ]),
  creative_exploration: new Set(['decision', 'candidates', 'open_questions']),
  risk_analysis: new Set([
    'decision', 'iron_dissent', 'validation_checklist', 'next_actions',
  ]),
  emotional_processing: new Set(['decision', 'open_questions']),
  value_clarification: new Set(['decision', 'open_questions']),
  strategy_planning: new Set([
    'decision', 'candidates', 'next_actions', 'attachment_citations',
  ]),
  conflict_resolution: new Set(['decision', 'candidates', 'next_actions']),
  knowledge_synthesis: new Set([
    'decision', 'attachment_citations', 'open_questions',
  ]),
  unknown_council: new Set(['decision']),
};

/**
 * isSectionRequired — true iff at least one of the matched scenes requires
 * the section (union semantics · PRD §7.7 表). always-required 5 sections
 * always return true regardless of scenes.
 */
export function isSectionRequired(
  section: ReportSection,
  scenes: readonly SceneSymbolId[] | undefined,
): boolean {
  if (ALWAYS_REQUIRED.includes(section)) return true;
  if (!scenes || scenes.length === 0) return false;
  for (const s of scenes) {
    const req = SCENE_REQUIRED_SECTIONS[s];
    if (req && req.has(section)) return true;
  }
  return false;
}

/**
 * P0-5 · attachment_citations 强校验.
 * knowledge_synthesis 必聚合到根 attachment_citations · 若 <3 → tooFew chip.
 */
export function selectAttachmentCitationsTooFew(
  scenes: readonly SceneSymbolId[] | undefined,
  citationCount: number,
): boolean {
  if (!scenes) return false;
  if (!scenes.includes('knowledge_synthesis')) return false;
  return citationCount < 3;
}
